import React, { useContext, useEffect, useState } from 'react';

import AJAX from 'common/AJAX';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import ErrorMessage from 'common/ErrorMessage';
import useMutation from 'common/hooks/useMutation';
import SourceFilter from 'common/subdomain/admin/AdminQueue/SourceFilters/SourceFilter';
import Button from 'common/uiV3/Button';
import Spinner from 'common/uiV3/Spinner';
import VStack from 'common/uiV3/VStack';

import type {
  GetGongTrackersResponse,
  GetGongWorkspacesResponse,
  GongSettings,
  SetGongFilterSettingsParams,
} from 'common/api/endpoints/gong';
import type { SuccessResponse } from 'common/api/responses';
import type { SelectBaseOption } from 'common/uiV3/Select';

type GongFilterSettingsProps = {
  settings: GongSettings;
};

const GongFilterSettings = ({ settings }: GongFilterSettingsProps) => {
  const showToast = useContext(ShowToastContext);

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [installation, setInstallation] = useState<Partial<GongSettings['installation']> | null>(
    settings.installation,
  );
  const [selectedWorkspaceIDs, setSelectedWorkspaceIDs] = useState<string[]>(
    installation?.workspaceIDs ?? [],
  );
  const [selectedTrackerIDs, setSelectedTrackerIDs] = useState<string[]>(
    installation?.trackerIDs ?? [],
  );

  const workspacesMutation = useMutation<GetGongWorkspacesResponse, void>({
    mutationFn: async () => AJAX.post('/api/gong/getWorkspaces'),
    isResponseSuccessful: (response) => 'workspaces' in response,
  });

  const trackersMutation = useMutation<GetGongTrackersResponse, void>({
    mutationFn: async () => AJAX.post('/api/gong/getTrackers'),
    isResponseSuccessful: (response) => 'trackers' in response,
  });

  const saveMutation = useMutation<SuccessResponse, SetGongFilterSettingsParams>({
    mutationFn: async (variables) => AJAX.post('/api/gong/setFilterSettings', variables),
    onSuccess: (_, variables) => {
      showToast('Filter settings saved', ToastTypes.success);
      setInstallation({
        workspaceIDs: variables.workspaceIDs ?? [],
        trackerIDs: variables.trackerIDs ?? [],
      });
    },
    onError: () =>
      showToast(
        'Something went wrong saving filter settings, please contact support.',
        ToastTypes.error,
      ),
  });

  useEffect(() => {
    setHasChanges(
      selectedWorkspaceIDs.length !== installation?.workspaceIDs?.length ||
        selectedTrackerIDs.length !== installation?.trackerIDs?.length ||
        selectedWorkspaceIDs.some((id) => !installation?.workspaceIDs?.includes(id)) ||
        selectedTrackerIDs.some((id) => !installation?.trackerIDs?.includes(id)),
    );
  }, [
    selectedWorkspaceIDs,
    selectedTrackerIDs,
    installation?.workspaceIDs,
    installation?.trackerIDs,
  ]);

  useEffect(() => {
    workspacesMutation.mutate();
    trackersMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () =>
    saveMutation.mutate({
      workspaceIDs: selectedWorkspaceIDs,
      trackerIDs: selectedTrackerIDs,
    });

  const workspaceOptions: SelectBaseOption[] =
    workspacesMutation.data?.workspaces.map((workspace) => ({
      label: workspace.name,
      value: workspace.id,
    })) ?? [];

  const trackerOptions: SelectBaseOption[] =
    trackersMutation.data?.trackers.map((tracker) => ({
      label: tracker.trackerName,
      value: tracker.trackerId,
    })) ?? [];

  const isLoading = workspacesMutation.isLoading || trackersMutation.isLoading;
  const hasError = workspacesMutation.isError || trackersMutation.isError;

  return (
    <VStack className="mt-8 gap-4">
      <VStack className="gap-2">
        <h2 className="text-lg font-semibold">Autopilot settings</h2>
        <p className="text-secondary-foreground">
          Use filters to specify which conversations are processed by Autopilot.
        </p>
      </VStack>
      <VStack className="max-w-[400px] gap-4">
        {isLoading && <Spinner />}
        {hasError && (
          <ErrorMessage>
            Something went wrong fetching options, please contact support.
          </ErrorMessage>
        )}
        {!workspacesMutation.isLoading && !hasError && (
          <SourceFilter
            options={workspaceOptions}
            value={selectedWorkspaceIDs ?? []}
            filterLabel="Filter by workspace"
            onChange={(value) => setSelectedWorkspaceIDs(value ?? [])}
          />
        )}
        {!trackersMutation.isLoading && !hasError && (
          <SourceFilter
            options={trackerOptions}
            value={selectedTrackerIDs ?? []}
            filterLabel="Filter by tracker"
            onChange={(value) => setSelectedTrackerIDs(value ?? [])}
          />
        )}
      </VStack>
      <Button
        size="default"
        variant="contained"
        color="primary"
        disabled={!hasChanges}
        isLoading={saveMutation.isLoading}
        onClick={handleSave}
        className="self-start">
        Save
      </Button>
    </VStack>
  );
};

export default GongFilterSettings;
